import Joi from 'joi';

import { pricingItemSchema } from '../../../../shared/modules/pricing-items/validators/schemas/pricing-item.schema';
import { createTagsSchema } from '../../../tags/validators/schemas/create-tags.schema';
import { VehicleDamageFaultEnum } from '../../enums/vehicle-damage-fault.enum';
import { VehicleDamageRecordCategoryEnum } from '../../enums/vehicle-damage-record-category.enum';
import { VehicleDamageSeverityEnum } from '../../enums/vehicle-damage-severity.enum';
import { VehicleDamageTypeEnum } from '../../enums/vehicle-damage-type.enum';

/**
 * Schema for existing vehicle damage record validation.
 */
export const updateVehicleDamageSchema = () =>
  Joi.object({
    id: Joi.alternatives().conditional(Joi.any().valid(null), {
      then: Joi.invalid(null).messages({
        'any.invalid': 'validations|vehicle-damage-id|cannot-be-null',
      }),
      otherwise: Joi.number().required().messages({
        'any.required': 'validations|vehicle-damage-id|required',
        'number.base': 'validations|vehicle-damage-id|must-be-number',
      }),
    }),
    recordCategory: Joi.alternatives().conditional(Joi.any().valid(null), {
      then: Joi.invalid(null).messages({
        'any.invalid':
          'validations|vehicle-damage-record-category|cannot-be-null',
      }),
      otherwise: Joi.string()
        .valid(...Object.values(VehicleDamageRecordCategoryEnum))
        .required()
        .messages({
          'any.required': 'validations|vehicle-damage-record-category|required',
          'any.only':
            'validations|vehicle-damage-record-category|invalid-value',
          'string.base':
            'validations|vehicle-damage-record-category|must-be-string',
          'string.empty':
            'validations|vehicle-damage-record-category|cannot-be-empty',
        }),
    }),

    vehicleId: Joi.when('recordCategory', {
      is: VehicleDamageRecordCategoryEnum.VEHICLE,
      then: Joi.alternatives()
        .conditional(Joi.any().valid(null), {
          then: Joi.invalid(null).messages({
            'any.invalid':
              'validations|vehicle-damage-vehicle-id|cannot-be-null',
          }),
          otherwise: Joi.number().required().messages({
            'any.required': 'validations|vehicle-damage-vehicle-id|required',
            'number.base':
              'validations|vehicle-damage-vehicle-id|must-be-number',
          }),
        })
        .required()
        .messages({
          'any.required': 'validations|vehicle-damage-vehicle-id|required',
        }),
      otherwise: Joi.allow(null),
    }),

    rentalAgreementId: Joi.when('recordCategory', {
      is: VehicleDamageRecordCategoryEnum.RENTAL_AGREEMENT,
      then: Joi.alternatives()
        .conditional(Joi.any().valid(null), {
          then: Joi.invalid(null).messages({
            'any.invalid':
              'validations|vehicle-damage-rental-agreement-id|cannot-be-null',
          }),
          otherwise: Joi.number().required().messages({
            'any.required':
              'validations|vehicle-damage-rental-agreement-id|required',
            'number.base':
              'validations|vehicle-damage-rental-agreement-id|must-be-number',
          }),
        })
        .required()
        .messages({
          'any.required':
            'validations|vehicle-damage-rental-agreement-id|required',
        }),
      otherwise: Joi.allow(null),
    }),

    fault: Joi.valid(...Object.values(VehicleDamageFaultEnum))
      .allow(null)
      .messages({
        'any.only': 'validations|vehicle-damage-fault|invalid-value',
      }),

    severity: Joi.valid(...Object.values(VehicleDamageSeverityEnum))
      .allow(null)
      .messages({
        'any.only': 'validations|vehicle-damage-severity|invalid-value',
      }),

    type: Joi.valid(...Object.values(VehicleDamageTypeEnum))
      .allow(null)
      .messages({
        'any.only': 'validations|vehicle-damage-type|invalid-value',
      }),

    notes: Joi.string().allow(null, '').messages({
      'string.base': 'validations|vehicle-damage-notes|must-be-string',
    }),

    policeReportFiled: Joi.boolean().allow(null).messages({
      'boolean.base':
        'validations|vehicle-damage-police-report-filed|must-be-boolean',
    }),

    policeReportExternalReference: Joi.string().allow(null, '').messages({
      'string.base':
        'validations|vehicle-damage-police-report-external-reference|must-be-string',
    }),

    estimatedCost: Joi.number().allow(null).messages({
      'number.base': 'validations|vehicle-damage-estimated-cost|must-be-number',
    }),

    tags: createTagsSchema().messages({
      'array.base': 'validations|vehicle-damage-tags|invalid-value',
    }),

    reportedAt: Joi.alternatives().conditional(Joi.any().valid(null), {
      then: Joi.invalid(null).messages({
        'any.invalid': 'validations|vehicle-damage-reported-at|cannot-be-null',
      }),
      otherwise: Joi.date().max('now').required().messages({
        'any.required': 'validations|vehicle-damage-reported-at|required',
        'date.base':
          'validations|vehicle-damage-reported-at|must-be-a-valid-date',
        'date.max': 'validations|vehicle-damage-reported-at|no-future-date',
      }),
    }),

    pricingItems: Joi.array().items(pricingItemSchema()).allow(null),

    files: Joi.any().allow(null),
  })
    // Ignore extra fields
    .unknown();
